/**
 * CheckInOutActions — 022-checkin-checkout-times
 *
 * Renders "Record check-in" and "Record check-out" action buttons for a booking.
 * Visibility rules:
 * - Record check-in:  visible when status === 'CONFIRMED'
 * - Record check-out: visible when status === 'CHECKED_IN'
 * Both buttons are hidden for terminal statuses (COMPLETED, CANCELLED, CHECKED_OUT).
 *
 * Design decisions:
 * - Each button is its own mutation call — no shared pending state.
 * - onSuccess / onError callbacks propagate to the parent so the parent can
 *   show a toast/banner without this component needing to own global state.
 */

import { useTranslation } from 'react-i18next';
import { LogIn, LogOut } from 'lucide-react';
import { useCheckIn, useCheckOut } from '../hooks/useBookings';
import { Booking, TERMINAL_BOOKING_STATUSES } from '../types/booking';

interface CheckInOutActionsProps {
  booking: Booking;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const CheckInOutActions = ({
  booking,
  onSuccess,
  onError,
}: CheckInOutActionsProps) => {
  const { t } = useTranslation(['pages', 'errors']);

  const checkInMutation  = useCheckIn();
  const checkOutMutation = useCheckOut();

  // Never show actions for terminal statuses
  if (TERMINAL_BOOKING_STATUSES.has(booking.status)) return null;

  const showCheckIn  = booking.status === 'CONFIRMED';
  const showCheckOut = booking.status === 'CHECKED_IN';

  if (!showCheckIn && !showCheckOut) return null;

  const handleCheckIn = async () => {
    try {
      await checkInMutation.mutateAsync(booking.bookingId);
      onSuccess?.(t('pages:checkInRecorded'));
    } catch {
      onError?.(t('errors:somethingWentWrong'));
    }
  };

  const handleCheckOut = async () => {
    try {
      await checkOutMutation.mutateAsync(booking.bookingId);
      onSuccess?.(t('pages:checkOutRecorded'));
    } catch {
      onError?.(t('errors:somethingWentWrong'));
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {showCheckIn && (
        <button
          type="button"
          onClick={handleCheckIn}
          disabled={checkInMutation.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          <LogIn size={15} />
          {checkInMutation.isPending
            ? t('pages:recordingCheckIn')
            : t('pages:recordCheckIn')}
        </button>
      )}

      {showCheckOut && (
        <button
          type="button"
          onClick={handleCheckOut}
          disabled={checkOutMutation.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          <LogOut size={15} />
          {checkOutMutation.isPending
            ? t('pages:recordingCheckOut')
            : t('pages:recordCheckOut')}
        </button>
      )}
    </div>
  );
};
